import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Button, ButtonProps, Stack } from '@rossum/ui/material';
import { GridFilterItem } from '@rossum/ui/x-data-grid-pro';
import { AddFilter, VERTICAL_SPACING } from './AddFilterButton';
import { FilterChips } from './FilterChips';
import { getInitialFilter } from './getInitialFilter';
import { getUpdatedExistingFilter } from './getUpdatedExistingFilter';
import {
  FilterColDef,
  FilterItem,
  isFilterContextSimple,
  ValidatedGridFilterItem,
} from './types';

type FilterPanelProps = {
  columns: FilterColDef[];
  handleFilterModelChange: (fm: GridFilterItem[]) => void;
  handleClearAllFilters: () => void;
  isLoading?: boolean;
  invalidFilters: ValidatedGridFilterItem[];
  validFilters: ValidatedGridFilterItem[];
  hasActiveQueue: boolean;
  forceEnableClearButton?: boolean;
  addFilterButtonProps?: {
    type: 'inline' | 'separate';
    buttonProps?: ButtonProps;
  };
  clearFilterButtonProps?: {
    type: 'inline' | 'separate';
    buttonProps?: ButtonProps;
  };
};

type ClearFiltersButtonProps = {
  label: string;
  onClick: () => void;
  buttonProps?: ButtonProps;
};

const ClearFiltersButton = ({
  label,
  onClick,
  buttonProps,
}: ClearFiltersButtonProps) => (
  <Button
    variant="text"
    color="secondary"
    sx={{
      textWrap: 'nowrap',
      flexShrink: 0,
    }}
    onClick={onClick}
    data-cy="all-documents-clear-all-filters-button"
    {...buttonProps}
  >
    {label}
  </Button>
);

export const FilterPanel = ({
  columns,
  handleFilterModelChange,
  handleClearAllFilters,
  isLoading,
  validFilters,
  invalidFilters,
  hasActiveQueue,
  forceEnableClearButton,
  addFilterButtonProps = { type: 'inline' },
  clearFilterButtonProps = { type: 'inline' },
}: FilterPanelProps) => {
  const intl = useIntl();

  const allFilters = invalidFilters.concat(validFilters);

  const transformedFilterContext = useMemo(() => {
    if (allFilters.length === 0) return null;
    return getInitialFilter(allFilters, columns);
  }, [allFilters, columns]);

  const changeFilterModel = useCallback(
    (gridFilterItems: GridFilterItem[], isReplaceAction?: boolean) => {
      const updatedFilterItems = getUpdatedExistingFilter({
        existingFilterItems: validFilters,
        incomingFilters: gridFilterItems,
        isReplaceAction,
      });
      handleFilterModelChange(updatedFilterItems.concat(invalidFilters));
    },
    [handleFilterModelChange, invalidFilters, validFilters]
  );

  const removeFilter = (field: string) => {
    const newFilterModel = allFilters.filter(item => item.field !== field);
    handleFilterModelChange(newFilterModel);
  };

  const applyFilter = ({
    column,
    filterContext,
    isReplaceAction,
  }: Omit<FilterItem, 'isValid'> & { isReplaceAction?: boolean }) => {
    const contextArray = isFilterContextSimple(filterContext)
      ? [filterContext]
      : filterContext;

    const gridFilterItems: GridFilterItem[] = contextArray.map(
      ({ operator, value }) => ({
        field: column.field,
        operator: operator.value,
        value,
      })
    );

    changeFilterModel(gridFilterItems, isReplaceAction);
  };

  const filterableColumns = useMemo(
    () =>
      columns.filter(c => {
        // filter for column already exists
        if (transformedFilterContext?.[c.field]) return false;

        // already on queue level and must not see queue filter
        if (hasActiveQueue && c.field === 'queue') return false;

        return true;
      }),
    [columns, transformedFilterContext, hasActiveQueue]
  );

  const areFiltersReady = !isLoading && !!transformedFilterContext;

  return (
    <Stack spacing={1} useFlexGap>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        justifyContent="space-between"
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
          sx={{ my: VERTICAL_SPACING }}
        >
          {areFiltersReady && !!transformedFilterContext ? (
            <FilterChips
              filters={transformedFilterContext}
              applyFilter={(filterContext, column) =>
                applyFilter({
                  column,
                  filterContext,
                  isReplaceAction: true,
                })
              }
              removeFilter={removeFilter}
              hideQueueChip={hasActiveQueue}
            />
          ) : null}

          {addFilterButtonProps.type === 'inline' && (
            <AddFilter
              columns={filterableColumns}
              onConfirm={applyFilter}
              isLoading={isLoading}
            />
          )}
        </Stack>
        {(areFiltersReady || forceEnableClearButton) &&
          clearFilterButtonProps.type === 'inline' && (
            <ClearFiltersButton
              label={intl.formatMessage({
                id: 'containers.filtering.buttons.clear',
              })}
              onClick={handleClearAllFilters}
            />
          )}
      </Stack>
      <Stack direction="row" spacing={1} useFlexGap>
        {clearFilterButtonProps.type === 'separate' && (
          <ClearFiltersButton
            label={intl.formatMessage({
              id: 'containers.filtering.buttons.clear',
            })}
            onClick={handleClearAllFilters}
            buttonProps={clearFilterButtonProps.buttonProps}
          />
        )}
        {addFilterButtonProps.type === 'separate' && (
          <AddFilter
            columns={filterableColumns}
            onConfirm={applyFilter}
            isLoading={isLoading}
            buttonProps={addFilterButtonProps.buttonProps}
          />
        )}
      </Stack>
    </Stack>
  );
};
